'use client'

import Link from '@/components/link-locale'
import { siteConfig } from '@/config/site'
import { useMyChannels } from '@/lib/api/hooks/use-channel'
import { GetUser } from '@/lib/api/hooks/use-user'
import { useAsideStore, useCreatorStore } from '@/store'
import { getAssetUrl } from '@/utils'
import clsx from 'clsx'
import { useLocale, useTranslations } from 'next-intl'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import React, { useMemo } from 'react'
import {
  AiOutlineClockCircle,
  AiOutlineDollar,
  AiOutlineHome,
  AiOutlineInstagram,
  AiOutlineLogout,
  AiOutlineStar,
  AiOutlineTikTok,
  AiOutlineTwitter,
  AiOutlineUser,
  AiOutlineUserSwitch,
} from 'react-icons/ai'
import {
  MdAllInbox,
  MdDynamicFeed,
  MdOutlineBookmarkBorder,
  MdOutlineSentimentSatisfied,
  MdOutlineSignalCellularAlt,
} from 'react-icons/md'
import {
  RiShoppingBag3Line,
  RiUserAddLine,
  RiUserSettingsLine,
} from 'react-icons/ri'
import { TbSettingsStar } from 'react-icons/tb'

const SocialItems = [
  {
    icon: <AiOutlineTikTok />,
    href: 'https://www.tiktok.com/@mfans.official',
  },
  {
    icon: <AiOutlineTwitter />,
    href: 'https://x.com/MfansofficialX',
  },
  {
    icon: <AiOutlineInstagram />,
    href: 'https://www.instagram.com/mfans.official/',
  },
]

export const metadata = {
  robots: 'noindex, nofollow',
}

export function SideNav() {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const relativePath = pathname.replace(`/${locale}`, '/').replace('//', '/')
  const searchParams = useSearchParams()

  const queryString = searchParams.toString()
  const currentHref = pathname + (queryString ? `?${queryString}` : '')

  const userNavItems = [
    {
      label: t('sideNav.profile'),
      href: '/user/profile',
      icon: <AiOutlineUser />,
    },
    {
      label: t('sideNav.viewHistory'),
      href: '/user/watch-history',
      icon: <AiOutlineClockCircle />,
    },
    {
      label: t('sideNav.myCollection'),
      href: '/user/favorite',
      icon: <MdOutlineBookmarkBorder />,
    },
    {
      label: t('sideNav.mySubscription'),
      href: '/user/subscribe',
      icon: <AiOutlineStar />,
    },
    {
      label: t('sideNav.unlocks'),
      href: '/user/unlock',
      icon: <RiShoppingBag3Line />,
    },
    {
      label: t('sideNav.viewPreferences'),
      href: '/user/category',
      icon: <MdOutlineSentimentSatisfied />,
    },
  ]

  const rookieNavItems = [
    {
      label: t('sideNav.becomeCreator'),
      href: '/user/identity',
      icon: <RiUserAddLine />,
    },
  ]

  const creatorNavItems = [
    {
      label: t('sideNav.switch'),
      href: '/creator/channels',
      icon: <AiOutlineUserSwitch />,
    },
    {
      label: t('sideNav.myChannel'),
      href: '',
      icon: <AiOutlineHome />,
    },
    {
      label: t('sideNav.mediaLibrary'),
      href: '/media-library',
      icon: <MdAllInbox />,
    },
    {
      label: t('sideNav.myWorks'),
      href: '/videos',
      icon: <MdDynamicFeed />,
    },
    {
      label: t('sideNav.subscriptionSettings'),
      href: '/premium',
      icon: <TbSettingsStar />,
    },
    {
      label: t('sideNav.revenueManagement'),
      href: '/revenue',
      icon: <AiOutlineDollar />,
    },
    {
      label: t('sideNav.dataAnalysis'),
      href: '/statistic',
      icon: <MdOutlineSignalCellularAlt />,
    },
    {
      label: t('sideNav.withdrawSettings'),
      href: '/creator/finance',
      icon: <RiUserSettingsLine />,
    },
  ]

  const userSuffixNavItems = [
    {
      label: t('common.logout'),
      href: '/logout',
      icon: <AiOutlineLogout />,
    },
  ]

  const channelId = useCreatorStore(state => state.channelId)

  const { data: user } = GetUser()
  const { data: channels } = useMyChannels()

  const isGuest = user === undefined

  const channel = useMemo(() => {
    if (channels) {
      return channels.list.find(item => item.id === channelId)
    }
  }, [channels, channelId])

  const avatar = useMemo(() => {
    if (isGuest) {
      return getAssetUrl(siteConfig.placeholder.avatar)
    }
    if (channel) {
      return channel.avatar || getAssetUrl(siteConfig.placeholder.avatar)
    }
    return user.avatar || getAssetUrl(siteConfig.placeholder.avatar)
  }, [user, isGuest, channel])

  const displayName = useMemo(() => {
    if (isGuest) {
      return ''
    }
    if (channel) {
      return channel.name
    }
    return user.nickname
  }, [user, isGuest, channel])

  const displayCode = useMemo(() => {
    if (isGuest) {
      return ''
    }
    if (channel) {
      return channel.code
    }
    return user.code
  }, [user, isGuest, channel])

  const navItems = useMemo(() => {
    let array = []
    if (!isGuest) {
      if (user.role === 'user') {
        array = user.isApply
          ? [...userNavItems, ...userSuffixNavItems]
          : [...userNavItems, ...rookieNavItems, ...userSuffixNavItems]
      } else if (user.role === 'creator') {
        if (channelId && channel !== undefined) {
          const temp = creatorNavItems.map(item => ({
            ...item,
            href: item.href.startsWith('/creator/')
              ? item.href
              : `/creator/channel${item.href}`,
          }))
          array = [...userNavItems, ...temp, ...userSuffixNavItems]
        } else {
          array = [
            ...userNavItems,
            ...creatorNavItems.filter(item =>
              item.href.startsWith('/creator/'),
            ),
            ...userSuffixNavItems,
          ]
        }
      }
    }
    return array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isGuest, locale, channelId, channel])

  const asideVisible = useAsideStore(state => state.visible)
  const setAsideVisible = useAsideStore(state => state.setVisible)

  const onLoginClick = () => {
    router.push(`/login?from=${currentHref}`)
  }

  return (
    <div
      className={clsx([
        'fixed left-0 top-0 z-40 h-dvh w-dvw bg-black/50 lg:relative lg:block lg:h-auto lg:w-[220px] lg:bg-inherit',
        asideVisible ? 'block' : 'hidden',
      ])}
      onClick={() => setAsideVisible(false)}
    >
      <div className='flex h-full w-[300px] flex-col overflow-y-auto bg-[#27272E] pt-5 lg:min-h-[800px] lg:w-full lg:rounded-[20px]'>
        {/* user info */}
        <div className='flex h-[52px] w-full gap-x-2.5 border-y border-[#595968] pl-3'>
          <img src={avatar} className='h-[50px] w-[50px]' alt='avatar' />
          <div className='flex flex-col justify-center gap-y-1 text-[#F9F9F9]'>
            {isGuest ? (
              <span className='text-xs font-semibold'>
                {t('sideNav.guestMode')}
              </span>
            ) : (
              <>
                <span className='text-xs font-semibold'>{displayName}</span>
                <span className='text-[10px] font-normal'>@{displayCode}</span>
              </>
            )}
          </div>
        </div>
        {/* user info end */}

        {/* nav */}
        <div className='flex-1'>
          <div className='flex flex-col'>
            {navItems.map((item, index) => {
              const isActive = item.href === relativePath
              return (
                <div
                  key={item.href}
                  className='group flex h-12 w-full border-[#595968] last:border-b'
                >
                  <Link href={item.href} className='flex w-full'>
                    <div
                      className={clsx([
                        'w-3 group-hover:bg-primary',
                        { 'bg-primary': isActive },
                      ])}
                    ></div>
                    <div
                      className={clsx([
                        'flex flex-1 items-center gap-x-2.5 border-b border-l border-[#595968] pl-5',
                        { 'border-b-0': index === navItems.length - 1 },
                      ])}
                    >
                      <span
                        className={clsx([
                          'text-2xl text-[#F9F9F9] group-hover:text-primary',
                          { 'text-primary': isActive },
                        ])}
                      >
                        {item.icon}
                      </span>
                      <span
                        className={clsx([
                          'text-sm font-semibold text-[#F9F9F9] group-hover:text-primary',
                          { 'text-primary': isActive },
                        ])}
                      >
                        {item.label}
                      </span>
                    </div>
                  </Link>
                </div>
              )
            })}
          </div>
        </div>
        {/* nav end */}

        {/* login */}
        {isGuest && (
          <div className='flex min-h-[118px] w-full flex-col gap-y-2 border-t border-[#595968] px-3 py-5'>
            <div className='flex gap-x-2'>
              <button
                onClick={onLoginClick}
                className='flex h-9 w-full flex-1 items-center justify-center rounded-lg bg-[#FF3C8E] text-sm font-semibold text-white'
              >
                {t('common.login')}
              </button>
              <Link href='/login?mode=register' className='flex-1'>
                <button className='flex h-9 w-full items-center justify-center rounded-lg bg-white text-sm font-semibold text-black'>
                  {t('common.register')}
                </button>
              </Link>
            </div>
            <p
              className='w-full text-center text-xs'
              dangerouslySetInnerHTML={{ __html: t('sideNav.guestTip') }}
            />
          </div>
        )}
        {/* login end */}

        {/* social */}
        <div className='flex h-[60px] w-full justify-center gap-x-5 border-t border-[#595968] py-5'>
          {SocialItems.map(item => (
            <Link href={item.href} key={item.href} target='_blank'>
              <span className='text-xl text-[#595968] hover:text-[#FF3C8E]'>
                {item.icon}
              </span>
            </Link>
          ))}
        </div>
        {/* social end */}
      </div>
    </div>
  )
}
